/**
 * Gruppierung der Statusauswahl nach Hauptkategorie (hawk#606).
 *
 * Status tragen seit hawk#606 eine Haupt- und Unterkategorie ("3.0 geplant" =
 * main_category 3, sub_category 0, name "geplant"). Die Auswahl wird nach
 * Hauptkategorie gruppiert; die Gruppenüberschrift ist das Label des 0er-Status
 * der Gruppe, der 0er-Status selbst bleibt als Eintrag wählbar. Legacy-Status
 * ohne Kategorie landen in der Gruppe „Ohne Kategorie“ am Ende.
 */

/** Minimale Status-Form, die für Anzeige und Gruppierung gebraucht wird. */
export interface CategorizedStatus {
  id?: number;
  name: string;
  label?: string;
  main_category?: number | null;
  sub_category?: number | null;
}

/** Ein Eintrag der gruppierten Auswahl: der Status plus Anzeigename und Gruppe. */
export type StatusSelectOption<T extends CategorizedStatus = CategorizedStatus> = T & {
  label: string;
  group: string;
  disabled?: boolean;
};

export const UNCATEGORIZED_STATUS_GROUP = "Ohne Kategorie";

/** Anzeigename eines Status: Label vom Backend, sonst (ältere Antworten) der Name. */
export function statusLabel(status: Pick<CategorizedStatus, "name" | "label"> | null | undefined): string {
  return status?.label || status?.name || "";
}

function hasCategory(status: CategorizedStatus): boolean {
  return status.main_category !== null && status.main_category !== undefined;
}

// Sortierschlüssel: fehlende Kategorie ans Ende
function categoryKey(value: number | null | undefined): number {
  return value === null || value === undefined ? Number.POSITIVE_INFINITY : value;
}

/**
 * Bereitet Status für die gruppierte Auswahl (ng-select `groupBy="group"`) auf.
 *
 * - sortiert stabil nach Haupt- und Unterkategorie, Legacy zuletzt (die
 *   Reihenfolge des Backends bleibt bei Gleichstand erhalten)
 * - Gruppe = Label des 0er-Status der Hauptkategorie, sonst "<main>.x"
 * - Legacy-Status (main_category null) → „Ohne Kategorie“
 * - optional: `isDisabled` markiert einzelne Einträge als nicht wählbar
 */
export function groupStatuses<T extends CategorizedStatus>(
  statuses: readonly T[] | null | undefined,
  isDisabled?: (status: T) => boolean,
): StatusSelectOption<T>[] {
  const list = [...(statuses ?? [])];

  // Gruppenüberschriften aus den 0er-Status je Hauptkategorie
  const groupHeaders = new Map<number, string>();
  for (const status of list) {
    if (hasCategory(status) && status.sub_category === 0 && !groupHeaders.has(status.main_category)) {
      groupHeaders.set(status.main_category, statusLabel(status));
    }
  }

  const groupName = (status: T): string => {
    if (!hasCategory(status)) return UNCATEGORIZED_STATUS_GROUP;
    return groupHeaders.get(status.main_category) ?? `${status.main_category}.x`;
  };

  return list
    .map((status, index) => ({ status, index }))
    .sort(
      (a, b) =>
        categoryKey(a.status.main_category) - categoryKey(b.status.main_category) ||
        categoryKey(a.status.sub_category) - categoryKey(b.status.sub_category) ||
        a.index - b.index,
    )
    .map(({ status }) => {
      const option: StatusSelectOption<T> = {
        ...status,
        label: statusLabel(status),
        group: groupName(status),
      };
      if (isDisabled) option.disabled = isDisabled(status);
      return option;
    });
}

/** Suche der Auswahl: über das Label (enthält Kategorie und Bezeichnung). */
export function statusSearchFn(term: string, option: StatusSelectOption): boolean {
  return option.label.toLowerCase().includes((term ?? "").trim().toLowerCase());
}

/** Eine Phase des Ticket-Ablaufs (Hauptkategorie) mit ihren Status. */
export interface StatusPhase<T extends CategorizedStatus = CategorizedStatus> {
  /** Hauptkategorie, null = Legacy-Status ohne Kategorie */
  main: number | null;
  /** Bezeichnung der Phase: title des 0er-Status, sonst "<main>.x" bzw. „Ohne Kategorie“ */
  title: string;
  /** Farbe des 0er-Status (bzw. des ersten Status) als Markierung */
  color: string | null;
  statuses: StatusSelectOption<T>[];
}

/**
 * Teilt die Status in Phasen nach Hauptkategorie auf (für die zweistufige
 * Statusauswahl der Ticketseite). Reihenfolge wie `groupStatuses`, Legacy zuletzt.
 */
export function statusPhases<T extends CategorizedStatus & { title?: string; color?: string }>(
  statuses: readonly T[] | null | undefined,
  isDisabled?: (status: T) => boolean,
): StatusPhase<T>[] {
  const phases: StatusPhase<T>[] = [];
  for (const option of groupStatuses(statuses, isDisabled)) {
    const main = hasCategory(option) ? (option.main_category as number) : null;
    let phase = phases[phases.length - 1];
    if (!phase || phase.main !== main) {
      phase = { main, title: "", color: null, statuses: [] };
      phases.push(phase);
    }
    phase.statuses.push(option);
  }

  for (const phase of phases) {
    const head = phase.statuses.find((s) => s.sub_category === 0) ?? null;
    if (phase.main === null) {
      phase.title = UNCATEGORIZED_STATUS_GROUP;
    } else {
      phase.title = head ? statusTitle(head) : `${phase.main}.x`;
    }
    phase.color = (head ?? phase.statuses[0])?.color ?? null;
  }
  return phases;
}

/** Bezeichnung ohne Kategorie ("Geplant" statt "3.0 Geplant"). */
export function statusTitle(status: CategorizedStatus & { title?: string }): string {
  if (status.title) return status.title;
  const label = statusLabel(status);
  return hasCategory(status) ? label.replace(/^\s*\d+[.,]\d+\s*/, "") : label;
}

/** Kategorie-Code "3.0", leer bei Legacy-Status. */
export function statusCode(status: CategorizedStatus): string {
  return hasCategory(status) ? `${status.main_category}.${status.sub_category}` : "";
}
